import {
    SyntheticEvent,
    ReactElement,
    useState,
    useMemo,
    MouseEvent,
    useCallback,
    useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Row, SortingRule, Column } from 'react-table';
import clsx from 'clsx';

import { Button, Grid, Box, IconButton } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';

import {
    ToggleButtons,
    CustomTable,
    CircularProgress,
    ThemeCheckboxesDropdown,
} from 'src/components/base';
import { getRowId, generateButtonFilterValues, getSortingPositions } from 'src/shared/utils';
import { FilterValues, TableNames, GlobalStoreActions } from 'src/shared/constants';
import { useGlobalStore } from 'src/shared/contexts';
import {
    useGetEntityItems,
    useProcessEntity,
    useDownloadEntity,
    useGetTableSettings,
    useUpdateTableSettings,
    useDearchiveEntities,
} from 'src/shared/hooks';
import { RegistrationItem, TableColumnSettings } from 'src/shared/types';
import { withDndContext } from 'src/shared/hocs';
import { useDebounce } from 'src/lib/custom-hooks/use-debounce';
import { usePageStyles } from '../pages-styles';
import { generateNewRegistrationTableConfig } from './new-registration-table-config';
import { useColumnVisibilityState } from './hooks/use-column-visibility-state';
import { useColumnDnd } from '../signing-off/hooks/use-column-dnd';
import { newRegistrationDefaultOrderColumn } from './new-registration-default-columns-order';
import { useApiInstance } from 'src/security/api-instance';

const languageNamespaces = ['common', 'new-registration', 'formatted-values'];

const TableWithColumnDnd = withDndContext(CustomTable);

export const NewRegistrationPage = (): ReactElement => {
    const pageClasses = usePageStyles();
    const apiInstance = useApiInstance();
    const { t } = useTranslation(languageNamespaces);
    const { dispatch, store } = useGlobalStore();
    const [filterValue, setFilterValue] = useState<string>(FilterValues.NEW);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [paginationState, setPaginationState] = useState<{ page: number; rowsPerPage: number }>({
        page: 0,
        rowsPerPage: 10,
    });
    const [sorting, setSorting] = useState<SortingRule<string> | null>(null);
    const [isDndLocked, setDndLocked] = useState<boolean>(true);
    const [isSettingsDropdownChanged, setSettingsDropdownChanged] = useState<boolean>(false);

    const debouncedSearchValue = useDebounce(store.insuranceConfirmationSearch.value);

    const { isLoading, data, getEntities } = useGetEntityItems<RegistrationItem>({
        searchTerm: debouncedSearchValue,
        page: paginationState.page,
        rowsPerPage: paginationState.rowsPerPage,
        sortField: sorting?.id || null,
        sortDesc: typeof sorting?.desc === 'boolean' ? sorting?.desc : null,
        processed: filterValue === FilterValues.ARCHIVED,
        url: 'insuranceConfirmationRegistrations',
    });

    const { isLoading: isDownloadLoading, download } = useDownloadEntity({
        insuranceConfirmationIds: selectedItems,
        url: 'downloadInsuranceConfirmationRegistrations',
    });

    const { isLoading: isDearchiveLoading, dearchive } = useDearchiveEntities({
        insuranceConfirmationIds: selectedItems,
        url: 'dearchiveInsuranceConfirmationRegistrations',
        onSuccess: getEntities,
    });

    const {
        isLoading: isGettingTableSettingsLoading,
        settings,
        requestTableSettings,
    } = useGetTableSettings({
        tableName: TableNames.Registrations,
    });

    const { isLoading: isUpdateSettingsLoading, updateTableSettings } = useUpdateTableSettings({
        tableName: TableNames.Registrations,
        onError: requestTableSettings,
    });

    const { visibilitySettingsList, hiddenColumns, handleColumnSettingsList } =
        useColumnVisibilityState({ settings, setSettingsDropdownChanged });

    useEffect(() => {
        if (typeof dispatch === 'function') {
            dispatch({
                type: GlobalStoreActions.updateConfirmationInsurance,
            });
        }
    }, [paginationState, debouncedSearchValue, sorting, filterValue]);

    const dragEndCallback = (columnPositions: { [key: string]: number }): void => {
        updateTableSettings(
            visibilitySettingsList
                .map((item) => ({ name: item.value, isShown: item.checked }))
                .sort(
                    (
                        a: Omit<TableColumnSettings, 'position'>,
                        b: Omit<TableColumnSettings, 'position'>
                    ) => {
                        const key = 'name';
                        const aPosition = getSortingPositions(a, columnPositions, key);
                        const bPosition = getSortingPositions(b, columnPositions, key);
                        return aPosition > bPosition ? 1 : -1;
                    }
                )
        );
    };

    const { onDragStart, onDragEnd, onDragUpdate, startPosition, hoverPosition, columnPositions } =
        useColumnDnd({
            settings,
            dragEndCallback,
            defaultState: newRegistrationDefaultOrderColumn,
        });

    const onProcessSuccess = (): void => {
        getEntities();
        setSelectedItems([]);
    };

    const { isLoading: isProcessLoading, process } = useProcessEntity({
        insuranceConfirmationIds: selectedItems,
        onSuccess: onProcessSuccess,
        onInsuranceConfirmationChanged: getEntities,
        processUrl: 'processInsuranceConfirmationRegistrations',
        downloadUrl: 'downloadInsuranceConfirmationRegistrations',
    });

    const handleChange = (_: SyntheticEvent, newFilter: string): void => {
        if (newFilter) {
            setFilterValue(newFilter);
        }
    };

    const onChangePage = (_: MouseEvent<HTMLButtonElement> | null, page: number) => {
        setPaginationState({ ...paginationState, page });
    };

    const handleChangeRowsPerPage = (e: SyntheticEvent): void => {
        setPaginationState({
            rowsPerPage: parseInt((e.target as HTMLInputElement).value),
            page: 0,
        });
    };

    const handleRowSelection = (value: (Row<object> | undefined)[]): void => {
        setSelectedItems(value.map((item) => (item as { id: string }).id));
    };

    const handleSort = useCallback((sortBy?: SortingRule<string>): void => {
        setSorting(sortBy || null);
    }, []);

    const onDownload = () => {
        download();
    };

    const onBackToOpen = (): void => {
        dearchive();
    };

    const handleColumnSettingsListClose = (): void => {
        if (isSettingsDropdownChanged) {
            updateTableSettings(
                visibilitySettingsList
                    .map((item) => ({ name: item.value, isShown: item.checked }))
                    .sort(
                        (
                            a: Omit<TableColumnSettings, 'position'>,
                            b: Omit<TableColumnSettings, 'position'>
                        ) => {
                            const key = 'name';
                            const aPosition = getSortingPositions(a, columnPositions, key);
                            const bPosition = getSortingPositions(b, columnPositions, key);
                            return aPosition > bPosition ? 1 : -1;
                        }
                    )
            );
        }
    };

    const handleResetSettings = (): void => {
        (async () => {
            await updateTableSettings([]);
            requestTableSettings();
        })();
    };

    const buttonsList = useMemo(() => {
        return generateButtonFilterValues(t);
    }, [t]);

    const columns = useMemo(
        () =>
            generateNewRegistrationTableConfig({
                t,
                startPosition,
                hoverPosition,
                columnPositions,
                isDndLocked,
            }).sort((a: Column<object>, b: Column<object>) => {
                const key = 'id';
                const aPosition = getSortingPositions(a, columnPositions, key);
                const bPosition = getSortingPositions(b, columnPositions, key);
                return aPosition > bPosition ? 1 : -1;
            }),
        [t, startPosition, hoverPosition, columnPositions, isDndLocked]
    );

    return (
        <div className={pageClasses.pageContainer}>
            <div className={pageClasses.filterPanel}>
                {!isDndLocked && (
                    <Box mr={2}>
                        <Button
                            startIcon={
                                <CircularProgress
                                    isLoading={
                                        isGettingTableSettingsLoading || isUpdateSettingsLoading
                                    }
                                />
                            }
                            disabled={isGettingTableSettingsLoading || isUpdateSettingsLoading}
                            className={clsx(
                                pageClasses.settingButton,
                                pageClasses.resetSettingsButton
                            )}
                            onClick={handleResetSettings}
                        >
                            {t('common:resetSettings')}
                        </Button>
                    </Box>
                )}
                <IconButton
                    className={pageClasses.settingButton}
                    onClick={() => setDndLocked(!isDndLocked)}
                >
                    {isDndLocked ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />}
                </IconButton>
                <ThemeCheckboxesDropdown<TableColumnSettings>
                    languageNamespaces={languageNamespaces}
                    itemsList={visibilitySettingsList}
                    buttonIcon={
                        isGettingTableSettingsLoading ? (
                            <CircularProgress isLoading={isGettingTableSettingsLoading} />
                        ) : (
                            <SettingsOutlinedIcon />
                        )
                    }
                    iconButtonClass={pageClasses.settingButton}
                    setItemsList={handleColumnSettingsList}
                    isButtonDisabled={isGettingTableSettingsLoading}
                    onCloseHandler={handleColumnSettingsListClose}
                />
                <ToggleButtons
                    buttonsList={buttonsList}
                    value={filterValue}
                    handleChange={handleChange}
                />
            </div>
            {!isGettingTableSettingsLoading && (
                <TableWithColumnDnd
                    selectionEnabled
                    withPagination
                    hiddenColumns={hiddenColumns}
                    columns={columns}
                    data={data.items}
                    isLoading={isLoading}
                    noDataMessage='new-registration:noData'
                    languageNamespaces={languageNamespaces}
                    page={paginationState.page}
                    rowsPerPage={paginationState.rowsPerPage}
                    onChangePage={onChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    getRowId={getRowId}
                    handleRowSelection={handleRowSelection}
                    count={data.total}
                    handleSort={handleSort}
                    sortField={sorting?.id}
                    sortDesc={sorting?.desc}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onDragUpdate={onDragUpdate}
                    droppableId='new-registration-page'
                    direction='horizontal'
                />
            )}
            {isGettingTableSettingsLoading && (
                <Grid container alignItems='center' justifyContent='center'>
                    <CircularProgress isLoading={isGettingTableSettingsLoading} />
                </Grid>
            )}
            {filterValue === FilterValues.NEW && (
                <Button
                    disabled={isProcessLoading || !selectedItems?.length}
                    color='secondary'
                    variant='contained'
                    className={pageClasses.processButton}
                    startIcon={<CircularProgress isLoading={isProcessLoading} />}
                    onClick={process}
                >
                    {t('new-registration:process')}
                </Button>
            )}
            {filterValue === FilterValues.ARCHIVED && (
                <Box sx={{ textAlign: 'end' }}>
                    {apiInstance.settings?.canDearchiveInsuranceConfirmations && (
                        <Button
                            disabled={isDearchiveLoading || !selectedItems?.length}
                            color='secondary'
                            variant='contained'
                            className={pageClasses.processButton}
                            sx={{ marginRight: '8px' }}
                            startIcon={<CircularProgress isLoading={isDearchiveLoading} />}
                            onClick={onBackToOpen}
                        >
                            {t('new-registration:backToOpen')}
                        </Button>
                    )}
                    <Button
                        disabled={isDownloadLoading || !selectedItems?.length}
                        color='secondary'
                        variant='contained'
                        className={pageClasses.processButton}
                        startIcon={<CircularProgress isLoading={isDownloadLoading} />}
                        onClick={onDownload}
                    >
                        {t('new-registration:download')}
                    </Button>
                </Box>
            )}
        </div>
    );
};
